import { Navigate, useLocation } from 'react-router-dom';
import { Loader2, Box } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';

interface ProtectedRouteProps {
  children: React.ReactNode;
  /** Require user to have a tenant (workspace) */
  requireTenant?: boolean;
}

/**
 * Wrapper component that protects routes requiring authentication
 */
export function ProtectedRoute({ children, requireTenant = false }: ProtectedRouteProps) {
  const location = useLocation();
  // Use useAuth() instead of useAuthStore() to ensure initialization happens
  const { currentTenant, isLoading, isInitialized, isAuthenticated } = useAuth();

  // Show loading while auth is initializing
  if (!isInitialized || isLoading) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-background">
        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary">
          <Box className="h-7 w-7 text-primary-foreground" />
        </div>
        <div className="mt-4 flex items-center gap-2 text-foreground-muted">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading...
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  // Redirect to onboarding if tenant is required but user has none
  if (requireTenant && !currentTenant) {
    return <Navigate to="/onboarding" replace />;
  }

  return <>{children}</>;
}

/**
 * Wrapper for public routes that redirects authenticated users
 */
export function PublicRoute({ children }: { children: React.ReactNode }) {
  // Use useAuth() to ensure initialization happens
  const { isLoading, isInitialized, isAuthenticated } = useAuth();

  // Show loading while auth is initializing
  if (!isInitialized || isLoading) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-background">
        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary">
          <Box className="h-7 w-7 text-primary-foreground" />
        </div>
        <div className="mt-4 flex items-center gap-2 text-foreground-muted">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading...
        </div>
      </div>
    );
  }

  // Redirect to dashboard if already authenticated
  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}
